import { useEffect, useMemo, useRef, useState } from 'react';
import { IOptionAnyValue } from 'bricks/types';
import { useSlideout } from 'contexts/BackOfEnvelopeContext';
import { useGetBackOfEnvelopeScenarios } from 'views/DealDashboard/BOE/hooks/useGetBOEScenarios';
import { useGetBackOfEnvelope } from 'views/DealDashboard/BOE/hooks/useGetBOE';

const parseBoeMetric = (raw: string | null | undefined): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
};

interface UseBoeScenarioSelectionArgs {
    orgId: string;
    dealId: number;
    selectedScenario?: IOptionAnyValue;
    shouldStartFromBackOfEnvelope: boolean;
}

/**
 * Owns everything BOE-scenario-related for the Create Pro Forma modal:
 * scenario list + live refetch on slideout save, selected scenario detail fetch,
 * dropdown options (including empty state), parsed return metrics, and the
 * "open slideout" handler.
 */
export const useBoeScenarioSelection = ({
    orgId, dealId, selectedScenario, shouldStartFromBackOfEnvelope,
}: UseBoeScenarioSelectionArgs) => {
    const { data: scenariosData, refetch: refetchScenarios } = useGetBackOfEnvelopeScenarios({ orgId, dealId });
    const [latestScenarios, setLatestScenarios] = useState<typeof scenariosData | null>(null);

    const { data: fullSelectedScenario, refetch: refetchFullSelectedScenario } = useGetBackOfEnvelope({
        orgId,
        scenarioId: selectedScenario?.value,
        enabled: !!selectedScenario?.value && shouldStartFromBackOfEnvelope,
    });

    const {
        toggleSlideout, getSlideoutValues, setSelectedSlideoutScenarioId,
        setSelectedScenario: setSelectedScenarioForSlideout, slideoutSavedAt,
    } = useSlideout();

    const scenarioDropdownOptions = useMemo<IOptionAnyValue[]>(() => {
        const dataSource = latestScenarios ?? scenariosData;
        if (dataSource?.items?.length) {
            return dataSource.items.map(scenario => ({
                value: scenario.id,
                label: scenario.name,
                disabled: false,
                toggle: true,
            }));
        }
        const openEmptyState = () => toggleSlideout(undefined, dealId);
        return [{
            label: 'A Back-of-Envelope scenario needs to be created first',
            value: 'no-back-of-envelope',
            component: (
                <span
                    role="button"
                    tabIndex={0}
                    className="text-primary cursor--pointer"
                    onClick={openEmptyState}
                    onKeyDown={e => { if (e.key === 'Enter') openEmptyState(); }}
                >
                    A Back-of-Envelope scenario needs to be created first
                </span>
            ),
        }];
    }, [scenariosData, latestScenarios, toggleSlideout, dealId]);

    useEffect(() => {
        if (latestScenarios) setLatestScenarios(null);
    }, [scenariosData?.items, latestScenarios]);

    const lastHandledSavedAtRef = useRef<typeof slideoutSavedAt | null>(null);
    const selectedScenarioRef = useRef(selectedScenario);
    const shouldStartFromBackOfEnvelopeRef = useRef(shouldStartFromBackOfEnvelope);
    const refetchScenariosRef = useRef(refetchScenarios);
    const refetchFullSelectedScenarioRef = useRef(refetchFullSelectedScenario);

    useEffect(() => {
        selectedScenarioRef.current = selectedScenario;
        shouldStartFromBackOfEnvelopeRef.current = shouldStartFromBackOfEnvelope;
        refetchScenariosRef.current = refetchScenarios;
        refetchFullSelectedScenarioRef.current = refetchFullSelectedScenario;
    });

    useEffect(() => {
        if (!slideoutSavedAt || lastHandledSavedAtRef.current === slideoutSavedAt) return;
        lastHandledSavedAtRef.current = slideoutSavedAt;

        void (async () => {
            const { data } = await refetchScenariosRef.current();
            if (data?.items) setLatestScenarios(data);
            if (selectedScenarioRef.current?.value && shouldStartFromBackOfEnvelopeRef.current) {
                await refetchFullSelectedScenarioRef.current();
            }
        })();
    }, [slideoutSavedAt]);

    const openSlideout = () => {
        if (scenariosData?.items?.length && selectedScenario?.value) {
            setSelectedSlideoutScenarioId(selectedScenario.value);
            setSelectedScenarioForSlideout(selectedScenario);
            getSlideoutValues();
        }
        toggleSlideout(selectedScenario?.value);
    };

    return {
        scenariosData,
        fullSelectedScenario,
        scenarioDropdownOptions,
        returnOnCost: fullSelectedScenario ? parseBoeMetric(fullSelectedScenario.returnOnCost) : null,
        returnOnEquity: fullSelectedScenario ? parseBoeMetric(fullSelectedScenario.returnOnEquity) : null,
        profitReturn: fullSelectedScenario ? parseBoeMetric(fullSelectedScenario.profitReturn) : null,
        openSlideout,
    };
};
